/**
 * CallbackPage.tsx — the standalone callback lane at `/:clinicSlug/callback`.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the free callback launch lane. Reached from the marketing widget's
 * "Request a callback" action or the header's "Speak to a clinician now" — the
 * choice of lane is made up-front (on the widget), so the journey itself no
 * longer offers a mid-flow route picker. Attribution tags ride the query string
 * so callback leads attribute like form leads. Copy is category-level only
 * (R28) — the page never echoes the slug either.
 *
 * THE LEAD ID IS READ AND THEN REMOVED (2026-08-28). This lane took `?lead=`
 * off the query and left it there. `/:clinicSlug/callback` is an acquisition
 * surface and is allow-listed in `isTrackedPath`, so the container loads on it
 * and a leadId — a bearer capability that dereferences to a named patient's
 * height, weight and email — rode `page_location` into a container administered
 * outside this estate for as long as the visitor sat on the form. It now goes
 * through the same freeze-then-scrub the journey route uses; see
 * `handoffScrub.ts` for why one function owns both hand-off keys.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { parseAttribution } from './attribution';
import { CallbackForm } from './CallbackForm';
import { Card, Help, PageFrame, Title } from '../ui/kit';
import { scrubHandoffIdsFromUrl } from './handoffScrub';
import { JourneyBackButton } from './JourneyUi';
import { readLeadId } from './leadHandoff';

/** Callback request page: short form → confirmation in place. */
export function CallbackPage() {
  const { clinicSlug = 'weightloss' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const attribution = parseAttribution(location.search);
  // FROZEN AT MOUNT, and read through the SHARED reader so this lane accepts the
  // canonical `#lead=` fragment the widget actually mints and not only the
  // deprecated `?lead=` query form. Frozen because the scrub below removes it a
  // moment later: re-reading it per render would flip `needsEmail` mid-session,
  // grow an email field for a patient whose address is already on the Lead, and
  // post the callback with no leadId for the CRM sync to resolve a contact from.
  const [leadId] = useState<string | undefined>(() => readLeadId(location) ?? undefined);
  const canonicalJourney = `/${clinicSlug}${location.search}`;

  // The callback route is `isTrackedPath` ALLOW-LISTED (tracking/gtm.ts), so the
  // container loads here and reads `page_location` — and a leadId dereferences
  // to a named patient's height, weight and email via POST /v1/clinic/leads/
  // resume. Leaving it in the address bar for the whole time the visitor sits on
  // this form put it in that container, in the `Referer` on the next hop, and in
  // browser history. Same one-rewrite scrub the journey route uses; it runs on
  // the first commit, and a child's effect runs before its parent's, so it lands
  // before AppShell's `loadGtm()`.
  useEffect(() => {
    scrubHandoffIdsFromUrl(navigate, location);
  }, [navigate, location]);

  function backToJourney() {
    // The callback lane is always entered directly (from the marketing widget
    // or the header), never pushed from inside the journey — so never pop into
    // an unknown external or stale page. Replace this entry with the
    // deterministic clinic journey instead.
    //
    // The lead rides in history STATE, not on the URL. It has to travel somehow:
    // the journey resolves it into the height and weight the widget already
    // collected, and without it a visitor who backs out of this form lands on
    // Step 0 and retypes them. But putting it back in the query or the fragment
    // would hand the id straight back to `page_location` — undoing the scrub
    // above one button press later. History state is in-memory, never sent to a
    // server and never read by the container.
    navigate(canonicalJourney, { replace: true, state: leadId ? { leadId } : undefined });
  }

  return (
    <PageFrame narrow className="gps-journey-root">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--gps-s-4)' }}>
        <div style={{ marginTop: 'var(--gps-s-1)' }}>
          <JourneyBackButton onClick={backToJourney} />
        </div>
        <div style={{ minWidth: 0 }}>
          <Title>Request a free callback</Title>
          <Help style={{ marginBottom: 'var(--gps-s-6)' }}>
            Leave your number and our team will call you back to talk it through — free, and no pressure.
          </Help>
        </div>
      </div>
      <Card>
        <CallbackForm clinicSlug={clinicSlug} attribution={attribution} leadId={leadId} />
      </Card>
    </PageFrame>
  );
}
